<template>
  <div class="despesas-form">
    <!-- AI-Powered Content Extraction -->
    <div class="ai-section">
      <object-completion
        :label="t('Preencher com IA')"
        :description="t('Descreva a despesa')"
        :placeholder="t('Digite ou cole informações sobre a despesa aqui...')"
        :get-json-schema-to-fill="getSchemaToFill"
        :extra-informations-object="getExtraInformations"
        :success-callback="handleAISuccess"
        :error-callback="handleAIError"
        :cancel-label="t('Cancelar')"
        :send-label="t('Extrair Informações')"
        :debug="false">
      </object-completion>
    </div>

    <!-- General Information Box -->
    <box
      type="primary"
      :title="t('Informações gerais')"
      elem-id="boxInformacoesGeraisDespesa">

      <div class="form-group">
        <input-text
          data-testid="despesas-titulo-input"
          elem-id="despesa_titulo"
          :label="t('Título')"
          :placeholder="t('Título da despesa')"
          :validation="() => isTituloValid"
          :required="true"
          v-model="despesaData.despesa_titulo">
        </input-text>
      </div>

      <div class="form-row">
        <div class="form-field">
          <franchise-select
            :label="t('Franquia')"
            :placeholder="t('Escolha a franquia')"
            :validation="() => isFranquiaValid"
            v-model="despesaData.despesa_franquia_id">
          </franchise-select>
        </div>
        <div class="form-field">
          <select-list
            data-testid="despesas-status-select"
            elem-id="despesa_status"
            :label="t('Status')"
            :placeholder="t('Escolha o status')"
            :itens-select="statusOptions"
            :validation="() => isStatusValid"
            v-model="despesaData.despesa_status">
          </select-list>
        </div>
      </div>

      <div class="form-row">
        <div class="form-field">
          <select-list
            data-testid="despesas-categoria-select"
            elem-id="despesa_categoria_id"
            :label="t('Categoria')"
            :placeholder="t('Escolha uma categoria')"
            :itens-select="categoriasOptions"
            :validation="() => isCategoriaValid"
            v-model="despesaData.despesa_categoria_id">
          </select-list>
        </div>
        <div class="form-field">
          <input-currency
            data-testid="despesas-valor-input"
            elem-id="despesa_valor"
            :label="t('Valor')"
            :validation="() => isValorValid"
            v-model="despesaData.despesa_valor">
          </input-currency>
        </div>
      </div>

      <div class="form-row" v-if="exigeNotaFiscal || despesaData.despesa_nota_fiscal">
        <div class="form-field">
          <input-text
            data-testid="despesas-nota-fiscal-input"
            elem-id="despesa_nota_fiscal"
            :label="t('Número da nota fiscal')"
            :required="exigeNotaFiscal"
            :validation="() => isNotaFiscalValid"
            v-model="despesaData.despesa_nota_fiscal">
          </input-text>
        </div>
      </div>

      <div class="form-row" v-if="isEditMode">
        <div class="form-field">
          <input-text
            data-testid="despesas-parcela-input"
            elem-id="despesa_parcela"
            :label="t('Parcela')"
            :validation="() => true"
            :disabled="true"
            :value="parcelaDisplay">
          </input-text>
        </div>
        <div class="form-field">
          <input-number
            data-testid="despesas-qtd-parcelas-input"
            elem-id="despesa_qtd_parcelas_edit"
            :label="t('Qtd Parcelas')"
            :placeholder="t('Qtd parcelas')"
            :validation="() => isQtdParcelasValid"
            v-model="despesaData.despesa_qtd_parcelas">
          </input-number>
        </div>
      </div>

      <div class="form-row" v-else>
        <div class="form-field-full">
          <input-number
            data-testid="despesas-qtd-parcelas-input"
            elem-id="despesa_qtd_parcelas_new"
            :label="t('Qtd Parcelas')"
            :placeholder="t('Qtd parcelas')"
            :validation="() => isQtdParcelasValid"
            v-model="despesaData.despesa_qtd_parcelas">
          </input-number>
        </div>
      </div>

      <!-- Parcelas Info -->
      <inline-message
        v-if="showParcelasInfo"
        data-testid="despesas-parcelas-info"
        severity="info">
        <strong>{{ t('Atenção') }}:</strong> {{ t('Serão criadas') }}
        <strong>{{ despesaData.despesa_qtd_parcelas }}</strong> {{ t('parcelas de') }}
        <strong>{{ n(valorParcela, 'currency') }}</strong> {{ t('Total') }}:
        <strong>{{ n(valorTotal, 'currency') }}</strong>
      </inline-message>
    </box>

    <!-- Dates Box -->
    <box
      type="primary"
      :title="t('Datas')"
      elem-id="boxDatasDespesa">

      <div class="form-row">
        <div class="form-field">
          <input-date-time
            data-testid="despesas-data-lancamento-input"
            elem-id="despesa_data_lancamento"
            :label="t('Data de lançamento')"
            :validation="() => isDataLancamentoValid"
            v-model="despesaData.despesa_data_lancamento">
          </input-date-time>
        </div>
        <div class="form-field">
          <input-date
            data-testid="despesas-data-vencimento-input"
            elem-id="despesa_data_vencimento"
            :label="t('Data de vencimento')"
            :validation="() => isDataVencimentoValid"
            v-model="despesaData.despesa_data_vencimento">
          </input-date>
        </div>
      </div>
    </box>

    <!-- Description Box -->
    <box
      type="primary"
      :title="t('Descrição')"
      elem-id="boxDescricaoDespesa">

      <input-rich-text
        data-testid="despesas-descricao-input"
        elem-id="despesaDescricao"
        :show-toolbar="true"
        v-model="despesaData.despesa_descricao">
      </input-rich-text>
    </box>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import { useDespesas } from '../../composables/useDespesas';
import { useDateFormatter } from '../../composables/useDateFormatter';
import { useDespesasStatus } from '../../composables/useDespesasStatus';
import { useConfirmAction } from '../../composables/useConfirmAction';
import ObjectCompletion from '../../inputs/ObjectCompletion.vue';
import type { DespesaCategoria, DespesaStatusItem } from '../../types/despesas.types';
import type { Despesas_SalvarInput } from '../../contracts/Despesas_SalvarInput';

interface SelectOption {
  ID: string;
  Name: string;
  Description?: string;
  ExigeNf?: boolean;
}

interface DespesaFormState {
  despesa_id: number | null;
  despesa_titulo: string;
  despesa_franquia_id: number | string;
  despesa_status: string;
  despesa_categoria_id: number | string;
  despesa_valor: number;
  despesa_qtd_parcelas: number;
  despesa_data_lancamento: string;
  despesa_data_vencimento: string;
  despesa_descricao: string;
  despesa_parcela: number;
  despesa_nota_fiscal: string;
}

interface AICompletionResult {
  result?: {
    Dados?: Partial<DespesaFormState>;
  } & Partial<DespesaFormState>;
}

interface AICompletionError {
  error: string | Error;
}

const { t, n } = useI18n();
const { confirmAction } = useConfirmAction();
const { fetchCategorias, fetchDespesa, saveDespesa: saveDespesaAPI, baixarDespesa } = useDespesas();
// API → App: ISO 8601 UTC → hora local do navegador (o que o InputDateTime lê)
const { apiDateTimeToLocalInput, fromDateToApiDateTime } = useDateFormatter();
const { fetchStatus } = useDespesasStatus();

interface Props {
  isEditMode?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isEditMode: false
});

const emit = defineEmits<{
  'save-success': [];
  'action-success': [];
}>();

const isLoading = ref<boolean>(false);

const despesaData = reactive<DespesaFormState>({
  despesa_id: null,
  despesa_titulo: '',
  despesa_franquia_id: '',
  despesa_status: '1',
  despesa_categoria_id: '',
  despesa_valor: 0,
  despesa_qtd_parcelas: 1,
  despesa_data_lancamento: '',
  despesa_data_vencimento: '',
  despesa_descricao: '',
  despesa_parcela: 1,
  despesa_nota_fiscal: ''
});

const categoriasOptions = ref<SelectOption[]>([]);
const statusOptions = ref<SelectOption[]>([]);

// Computed properties
const parcelaDisplay = computed(() => {
  return `${despesaData.despesa_parcela} de ${despesaData.despesa_qtd_parcelas}`;
});

const showParcelasInfo = computed(() => {
  return !props.isEditMode && despesaData.despesa_qtd_parcelas > 1 && despesaData.despesa_valor > 0;
});

// O valor informado é o valor DE CADA parcela, não o total a ser dividido:
// `Despesas_model::cria_parcelas()` copia a linha inteira para cada mês sem
// tocar em `despesa_valor`. Dividir aqui fazia o aviso se contradizer na
// mesma frase — "12 parcelas de 2,49" com "Total: 359,88" (29,99 x 12).
const valorParcela = computed(() => {
  return despesaData.despesa_valor;
});

const valorTotal = computed(() => {
  return despesaData.despesa_valor * despesaData.despesa_qtd_parcelas;
});

// Validations
const isTituloValid = computed(() => {
  return despesaData.despesa_titulo && despesaData.despesa_titulo.trim().length > 0;
});

const isFranquiaValid = computed(() => {
  return despesaData.despesa_franquia_id && despesaData.despesa_franquia_id !== '';
});

const isStatusValid = computed(() => {
  return despesaData.despesa_status && despesaData.despesa_status !== '';
});

const isCategoriaValid = computed(() => {
  return despesaData.despesa_categoria_id && despesaData.despesa_categoria_id !== '';
});

// Categoria marcada "Exige nota fiscal" (produtos homologados, DS 2026-0073).
const exigeNotaFiscal = computed<boolean>(() =>
  categoriasOptions.value.some(c => c.ID === String(despesaData.despesa_categoria_id) && c.ExigeNf === true)
);

const isNotaFiscalValid = computed<boolean>(() => {
  return !exigeNotaFiscal.value || despesaData.despesa_nota_fiscal.trim() !== '';
});

const isValorValid = computed(() => {
  return despesaData.despesa_valor > 0;
});

const isQtdParcelasValid = computed(() => {
  return despesaData.despesa_qtd_parcelas > 0;
});

const isDataLancamentoValid = computed(() => {
  return despesaData.despesa_data_lancamento && despesaData.despesa_data_lancamento !== '';
});

const isDataVencimentoValid = computed(() => {
  return despesaData.despesa_data_vencimento && despesaData.despesa_data_vencimento !== '';
});

const canSave = computed(() => {
  return isTituloValid.value && 
         isFranquiaValid.value && 
         isStatusValid.value && 
         isCategoriaValid.value && 
         isNotaFiscalValid.value && 
         isValorValid.value && 
         isQtdParcelasValid.value && 
         isDataLancamentoValid.value && 
         isDataVencimentoValid.value && 
         !isLoading.value;
});

const loadOptions = async (incluirCategoriaId?: string | number) => {
  log('DespesasForm', 'loadOptions', 'Begin');
  try {
    // Load categorias and status options in parallel
    const [categoriasData, statusData] = await Promise.all([
      fetchCategorias(incluirCategoriaId),
      fetchStatus()
    ]);

    log('DespesasForm', 'loadOptions', 'Categorias data', categoriasData);
    log('DespesasForm', 'loadOptions', 'Status data', statusData);

    if (categoriasData) {
      categoriasOptions.value = (categoriasData as unknown as Array<{ Valor: string | number; Nome: string; Descricao?: string | null; ExigeNf?: boolean }>).map((categoria) => ({
        ID: String(categoria.Valor),
        Name: categoria.Nome,
        Description: categoria.Descricao ?? undefined,
        ExigeNf: categoria.ExigeNf === true
      })) || [];
      log('DespesasForm', 'loadOptions', 'Loaded categorias', categoriasOptions.value.length);
    } else {
      log('DespesasForm', 'loadOptions', 'Failed to load categorias - no data');
    }

    if (statusData) {
      statusOptions.value = statusData.map((status) => ({
        ID: String(status.despesa_status_id),
        Name: status.despesa_status_titulo
      })) || [];
      log('DespesasForm', 'loadOptions', 'Loaded status options', statusOptions.value.length);
    } else {
      log('DespesasForm', 'loadOptions', 'Failed to load status options - no data');
    }

  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    console.error('Error loading options:', error);
    eventBus.emit('globalMessage.error', t('Erro ao carregar opções: ') + message);
  }
};


const loadForEdit = async (despesaId: number): Promise<void> => {
  log('DespesasForm', 'loadForEdit', 'Begin', despesaId);
  try {
    isLoading.value = true;

    const data = await fetchDespesa(despesaId);

    // Always load fresh options when opening modal for edit; keep the
    // despesa's current category even if it was deactivated since.
    await loadOptions(data?.despesa_categoria_id);
    log('DespesasForm', 'loadForEdit', 'Despesa data', data);

    if (data) {
      Object.assign(despesaData, {
        // Convert numeric fields from string to number (defensively coerce raw API values)
        despesa_valor: parseFloat(String(data.despesa_valor)) || 0,
        despesa_qtd_parcelas: parseInt(String(data.despesa_qtd_parcelas), 10) || 1,
        despesa_parcela: parseInt(String(data.despesa_parcela), 10) || 1,
        despesa_id: data.despesa_id,
        despesa_titulo: data.despesa_titulo || '',
        despesa_franquia_id: data.despesa_franquia_id || '',
        despesa_status: String(data.despesa_status ?? '1'),
        despesa_categoria_id: data.despesa_categoria_id || '',
        despesa_data_lancamento: apiDateTimeToLocalInput(data.despesa_data_lancamento),
        despesa_data_vencimento: data.despesa_data_vencimento || '',
        despesa_descricao: data.despesa_descricao || '',
        despesa_nota_fiscal: data.despesa_nota_fiscal || ''
      });
    } else {
      throw new Error(t('Erro ao carregar despesa'));
    }

  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    console.error('Error loading despesa:', error);
    eventBus.emit('globalMessage.error', message);
  } finally {
    isLoading.value = false;
    log('DespesasForm', 'loadForEdit', 'Finished');
  }
};

const saveDespesa = async (): Promise<void> => {
  log('DespesasForm', 'saveDespesa', 'Attempt save');
  if (!canSave.value) return;

  try {
    isLoading.value = true;

    const payload: Despesas_SalvarInput = {
      despesa_id: despesaData.despesa_id ?? undefined,
      despesa_titulo: despesaData.despesa_titulo,
      despesa_franquia_id: Number(despesaData.despesa_franquia_id) || undefined,
      despesa_status: Number(despesaData.despesa_status) || undefined,
      despesa_categoria_id: Number(despesaData.despesa_categoria_id) || undefined,
      despesa_valor: despesaData.despesa_valor,
      despesa_qtd_parcelas: despesaData.despesa_qtd_parcelas,
      despesa_data_lancamento: despesaData.despesa_data_lancamento,
      despesa_data_vencimento: despesaData.despesa_data_vencimento,
      despesa_descricao: despesaData.despesa_descricao,
      despesa_nota_fiscal: despesaData.despesa_nota_fiscal.trim()
    };

    const response = await saveDespesaAPI(payload);
    log('DespesasForm', 'saveDespesa', 'Saved successfully', response);

    eventBus.emit('globalMessage.success', t('Despesa salva com sucesso!'));
    emit('save-success');
    // Note: Modal will emit despesa.saved event, not the form

  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    console.error('Error saving despesa:', error);
    eventBus.emit('globalMessage.error', message);
  } finally {
    isLoading.value = false;
    log('DespesasForm', 'saveDespesa', 'Finished');
  }
};

const darBaixa = async (despesaId: number): Promise<void> => {
  log('DespesasForm', 'darBaixa', 'Called with ID', despesaId);

  confirmAction({
    message: t('Tem certeza que deseja dar baixa nesta despesa?'),
    header: t('Confirmação'),
    icon: 'pi pi-question-circle',
    acceptLabel: t('Sim'),
    rejectLabel: t('Não'),
    onAccept: async () => {
      try {
        isLoading.value = true;
        log('DespesasForm', 'darBaixa', 'Baixando despesa', despesaId);

        const response = await baixarDespesa(despesaId);
        log('DespesasForm', 'darBaixa', 'Baixa success', response);

        eventBus.emit('globalMessage.success', t('Baixa realizada com sucesso!'));
        emit('action-success');
        // Note: Modal will emit despesa.saved event, not the form

      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        console.error('Error giving baixa:', error);
        eventBus.emit('globalMessage.error', message);
      } finally {
        isLoading.value = false;
      }
    }
  });
};

const setDefaultDates = (): void => {
  log('DespesasForm', 'setDefaultDates', 'Setting defaults when empty');
  const now = new Date();

  if (!despesaData.despesa_data_lancamento || despesaData.despesa_data_lancamento === '') {
    // hora LOCAL do navegador (toISOString daria a hora de Londres)
    despesaData.despesa_data_lancamento = fromDateToApiDateTime(now);
  }

  if (!despesaData.despesa_data_vencimento || despesaData.despesa_data_vencimento === '') {
    despesaData.despesa_data_vencimento = now.toISOString().slice(0, 10);
  }
};

const resetForm = (): void => {
  log('DespesasForm', 'resetForm', 'Resetting form');
  Object.assign(despesaData, {
    despesa_id: null,
    despesa_titulo: '',
    despesa_franquia_id: '',
    despesa_status: '1',
    despesa_categoria_id: '',
    despesa_valor: 0,
    despesa_qtd_parcelas: 1,
    despesa_data_lancamento: '',
    despesa_data_vencimento: '',
    despesa_descricao: '',
    despesa_parcela: 1,
    despesa_nota_fiscal: ''
  });

  setDefaultDates();
};

const initializeForNew = async (): Promise<void> => {
  log('DespesasForm', 'initializeForNew', 'Begin');
  await loadOptions();
  setDefaultDates();
};

const getDespesaData = (): DespesaFormState => {
  log('DespesasForm', 'getDespesaData', 'Expose data');
  return despesaData;
};

// AI Completion Methods
const getSchemaToFill = (): Record<string, unknown> => {
  log('DespesasForm', 'getSchemaToFill', 'Generating schema');
  
  // Define the schema that the AI should fill
  const schema = {
    despesa_titulo: {
      type: 'string',
      description: 'Título resumido da despesa',
      minLength: 3,
      example: 'Aluguel do escritório'
    },
    despesa_categoria_id: {
      type: 'string',
      description: 'Categoria da despesa. Escolha entre: ' + categoriasOptions.value.map(c => `"${c.ID}" (${c.Name})`).join(', '),
      example: '1'
    },
    despesa_valor: {
      type: 'number',
      description: 'Valor total da despesa em reais (número decimal)',
      minimum: 0,
      example: 1500.00
    },
    despesa_qtd_parcelas: {
      type: 'integer',
      description: 'Quantidade de parcelas. Se não mencionado, use 1.',
      minimum: 1,
      default: 1,
      example: 1
    },
    despesa_data_lancamento: {
      type: 'string',
      format: 'datetime',
      description: 'Data e hora do lançamento no formato YYYY-MM-DD HH:MM:SS. Se não especificado, use a data/hora atual.',
      example: '2024-01-15 14:30:00'
    },
    despesa_data_vencimento: {
      type: 'string',
      format: 'date',
      description: 'Data de vencimento no formato YYYY-MM-DD. Se não especificado, use a data atual.',
      example: '2024-01-31'
    },
    despesa_descricao: {
      type: 'string',
      description: 'Descrição detalhada da despesa, incluindo observações, notas fiscais, etc.',
      example: 'Pagamento do aluguel referente ao mês de janeiro de 2024'
    }
  };
  
  log('DespesasForm', 'getSchemaToFill', 'Schema', schema);
  return schema;
};

const getExtraInformations = (): Record<string, string> => {
  log('DespesasForm', 'getExtraInformations', 'Generating extra info');

  const extraInfo: Record<string, string> = {
    categorias_disponiveis: categoriasOptions.value.map(c => `${c.ID}: ${c.Name}`).join(', '),
    contexto: 'Este é um registro de despesa. Extraia as informações relevantes sobre a despesa.',
    instrucoes: [
      'Se a categoria não for mencionada, tente inferir com base na descrição.',
      'Se o valor não for mencionado, deixe como 0.',
      'Se as datas não forem mencionadas, use a data atual.',
      'Se a quantidade de parcelas não for mencionada, use 1.',
      'A descrição deve ser o mais detalhada possível.'
    ].join(' ')
  };
  
  log('DespesasForm', 'getExtraInformations', 'Extra info', extraInfo);
  return extraInfo;
};

const handleAISuccess = ({ result }: AICompletionResult): void => {
  log('DespesasForm', 'handleAISuccess', 'AI extraction successful', result);

  try {
    if (result && typeof result === 'object') {
      const data = (result.Dados ?? result) as Partial<DespesaFormState>;

      log('DespesasForm', 'handleAISuccess', 'Extracted data', data);

      if (data.despesa_titulo) {
        despesaData.despesa_titulo = data.despesa_titulo;
      }

      if (data.despesa_categoria_id) {
        despesaData.despesa_categoria_id = String(data.despesa_categoria_id);
      }

      if (data.despesa_valor !== undefined && data.despesa_valor !== null) {
        despesaData.despesa_valor = parseFloat(String(data.despesa_valor)) || 0;
      }

      if (data.despesa_qtd_parcelas) {
        despesaData.despesa_qtd_parcelas = parseInt(String(data.despesa_qtd_parcelas), 10) || 1;
      }

      if (data.despesa_data_lancamento) {
        despesaData.despesa_data_lancamento = data.despesa_data_lancamento;
      }

      if (data.despesa_data_vencimento) {
        despesaData.despesa_data_vencimento = data.despesa_data_vencimento;
      }

      if (data.despesa_descricao) {
        despesaData.despesa_descricao = data.despesa_descricao;
      }

      log('DespesasForm', 'handleAISuccess', 'Form updated with AI data', despesaData);
      eventBus.emit('globalMessage.success', t('Informações extraídas com sucesso!'));
    } else {
      log('DespesasForm', 'handleAISuccess', 'Invalid result format', result);
      eventBus.emit('globalMessage.warning', t('Resposta da IA em formato inesperado'));
    }
  } catch (error) {
    log('DespesasForm', 'handleAISuccess', 'Error processing AI result', error);
    console.error('Error processing AI result:', error);
    eventBus.emit('globalMessage.error', t('Erro ao processar resposta da IA'));
  }
};

const handleAIError = ({ error }: AICompletionError): void => {
  log('DespesasForm', 'handleAIError', 'AI extraction failed', error);
  const message = error instanceof Error ? error.message : String(error);
  eventBus.emit('globalMessage.error', t('Erro ao extrair informações: ') + message);
};

// Expose public methods for parent components
defineExpose({
  canSave,
  loadForEdit,
  saveDespesa,
  darBaixa,
  resetForm,
  initializeForNew,
  getDespesaData
});
</script>

<style scoped>
.ai-section {
  margin-bottom: 20px;
}

.form-group {
  margin-bottom: 15px;
}

.form-row {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-bottom: 20px;
}

.form-field {
  flex: 1 1 calc(50% - 8px);
  min-width: 250px;
}

.form-field-full {
  flex: 1 1 100%;
}

@media (max-width: 768px) {
  .form-field {
    flex: 1 1 100%;
  }
}
</style>
